import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Download, CheckCircle, FileText, Package } from "lucide-react";

export default function AuditorPackPage() {
  const { activeEntity, entities } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (entityFilter) params.entity_id = entityFilter;
        const { data: d } = await api.get("/reports/auditor-pack", { params });
        setData(d);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [entityFilter]);

  if (loading) return <div className="stratum-card p-12 text-center text-[#6B7280]">Assembling Auditor Pack...</div>;
  if (!data) return <div className="stratum-card p-12 text-center text-[#6B7280]">No data</div>;

  const c = data.components;

  return (
    <div className="space-y-6" data-testid="auditor-pack-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <Package size={24} className="inline mr-2 text-[#C8A961]" />
            Auditor Pack
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">One-click bundle of all audit-ready documents and evidence</p>
        </div>
        <div className="flex gap-3">
          <select className="stratum-input" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
            <option value="">Group (All)</option>
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <button onClick={() => window.print()} className="stratum-btn-primary flex items-center gap-2" data-testid="download-auditor-pack-btn">
            <Download size={14} /> Print / Download Pack
          </button>
        </div>
      </div>

      {/* Pack Header */}
      <div className="stratum-card overflow-hidden">
        <div className="p-6 bg-[#0B1020] text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl font-bold">{data.auditor_pack.title}</h2>
              <p className="text-white/50 text-sm mt-1">Period: {data.auditor_pack.period} | Prepared: {data.auditor_pack.generated_at?.substring(0, 10)}</p>
            </div>
            <div className="text-right">
              <div className={`font-mono text-3xl font-bold ${c.health_score.score >= 70 ? "text-[#065F46]" : "text-[#C8A961]"}`}>
                {c.health_score.score}/100
              </div>
              <div className="text-xs text-white/50">Health Score ({c.health_score.grade})</div>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="p-6 border-b border-[#E8E2D5]">
          <h3 className="font-display text-base font-semibold text-[#0B1020] mb-3">Table of Contents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {data.auditor_pack.contents.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm py-1">
                <CheckCircle size={14} className="text-[#065F46] flex-shrink-0" />
                <span className="text-[#1A1A1A]">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Component Summary Grid */}
        <div className="p-6">
          <h3 className="font-display text-base font-semibold text-[#0B1020] mb-4">Pack Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
              <div className="font-mono text-xl font-semibold tabular-nums text-[#0B1020]">{c.asset_register.count}</div>
              <div className="text-xs text-[#6B7280] mt-1">Assets on Register</div>
              <div className="font-mono text-xs tabular-nums text-[#C8A961] mt-0.5">Cost: R{(c.asset_register.total_cost/1000000).toFixed(1)}M</div>
            </div>
            <div className="p-4 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
              <div className="font-mono text-xl font-semibold tabular-nums text-[#0B1020]">{c.journals.count}</div>
              <div className="text-xs text-[#6B7280] mt-1">Journal Entries</div>
              <div className="text-xs text-[#6B7280] mt-0.5">Types: {c.journals.types.join(", ")}</div>
            </div>
            <div className="p-4 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
              <div className="font-mono text-xl font-semibold tabular-nums text-[#0B1020]">{c.disposals.count}</div>
              <div className="text-xs text-[#6B7280] mt-1">Disposals</div>
              <div className="font-mono text-xs tabular-nums text-[#6B7280] mt-0.5">P/L: R{(c.disposals.total_pl/1000).toFixed(0)}K</div>
            </div>
            <div className="p-4 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
              <div className="font-mono text-xl font-semibold tabular-nums text-[#0B1020]">{c.audit_trail.entries}</div>
              <div className="text-xs text-[#6B7280] mt-1">Audit Trail Entries</div>
            </div>
            <div className="p-4 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
              <div className="font-mono text-xl font-semibold tabular-nums text-[#0B1020]">{c.policy_document.sections}</div>
              <div className="text-xs text-[#6B7280] mt-1">Policy Sections</div>
              <div className="text-xs text-[#6B7280] mt-0.5">{c.policy_document.categories} categories</div>
            </div>
            <div className="p-4 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
              <div className="font-mono text-xl font-semibold tabular-nums text-[#0B1020]">{c.reassessments.count}</div>
              <div className="text-xs text-[#6B7280] mt-1">Reassessments</div>
              <div className="text-xs text-[#6B7280] mt-0.5">{c.reassessments.approved} approved, {c.reassessments.rejected} rejected</div>
            </div>
            <div className="p-4 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
              <div className="font-mono text-xl font-semibold tabular-nums text-[#0B1020]">{c.anomalies.total}</div>
              <div className="text-xs text-[#6B7280] mt-1">Anomalies</div>
              <div className="text-xs mt-0.5"><span className="text-[#991B1B]">{c.anomalies.critical} critical</span>, <span className="text-[#92400E]">{c.anomalies.warnings} warn</span></div>
            </div>
            <div className={`p-4 rounded-lg border ${c.depreciation_recon.is_reconciled ? "bg-[#065F46]/5 border-[#065F46]/20" : "bg-[#991B1B]/5 border-[#991B1B]/20"}`}>
              <div className="font-mono text-xl font-semibold tabular-nums">{c.depreciation_recon.is_reconciled ? "Reconciled" : "Variance"}</div>
              <div className="text-xs text-[#6B7280] mt-1">Dep Reconciliation</div>
            </div>
          </div>
        </div>
      </div>

      <div className="stratum-card p-4 bg-[#065F46]/5 border-l-4 border-l-[#065F46] text-sm text-[#065F46]">
        <strong>For the auditor:</strong> This pack contains all supporting evidence for the PPE balance. Every journal, disposal, reassessment, and policy change is audit-trailed. Use the Audit Log for detailed before/after state on any mutation.
      </div>
    </div>
  );
}
